import { useState } from "react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import {
  DEFAULT_WINDOW_WEEKS,
  calendarDaysUntil,
  formatShortDate,
  isEvergreenKind,
} from "./signalMeta";
import LifetimeFields, {
  acceptWeeksInput,
  committedWeeksInput,
  resolveLifetimeChoice,
} from "./LifetimeFields";

/** The picker's opening week count: the stored suggestion when it has one, rounded
 *  to whole weeks (min 1), else the kind default. */
function initialWeeks(recommendedValidUntil: string | null): number {
  if (!recommendedValidUntil) return DEFAULT_WINDOW_WEEKS;
  const days = calendarDaysUntil(recommendedValidUntil.slice(0, 10));
  return Math.max(1, Math.round(days / 7));
}

/**
 * The curator's lifetime choice with its own confirm and cancel, used inline by
 * Extend on a steering row. Pre-filled from recommendedValidUntil when present.
 * "Until superseded" (null) is only offered for the evergreen kinds.
 */
export default function LifetimePicker({
  kind,
  recommendedValidUntil,
  confirmLabel = "Accept",
  isPending = false,
  onConfirm,
  onCancel,
}: {
  kind: string;
  recommendedValidUntil: string | null;
  confirmLabel?: string;
  isPending?: boolean;
  onConfirm: (validUntil: string | null) => void;
  onCancel: () => void;
}) {
  const [openEnded, setOpenEnded] = useState(false);
  const [weeksInput, setWeeksInput] = useState(() => String(initialWeeks(recommendedValidUntil)));

  // Only the evergreen kinds may run open-ended; derived rather than reset in an effect.
  const effectiveOpenEnded = openEnded && isEvergreenKind(kind);
  const canConfirm = effectiveOpenEnded || weeksInput !== "";
  const until = weeksInput === "" ? null : resolveLifetimeChoice(weeksInput, effectiveOpenEnded);

  return (
    <div className="sig-picker">
      <LifetimeFields
        kind={kind}
        weeksInput={weeksInput}
        openEnded={effectiveOpenEnded}
        onWeeksChange={(raw) => acceptWeeksInput(raw) && setWeeksInput(raw)}
        onWeeksBlur={() =>
          setWeeksInput((w) => committedWeeksInput(w, String(DEFAULT_WINDOW_WEEKS)))
        }
        onOpenEndedChange={setOpenEnded}
        label="Let this steer for"
        hint={
          effectiveOpenEnded
            ? "Until a newer signal replaces it"
            : until
              ? `Until ${formatShortDate(until)}`
              : undefined
        }
      />
      {/* Signals / Steering · Extend: Cancel is the ghost capsule, Extend the primary, both 32. */}
      <div className="sig-picker__acts">
        <KitButton variant="ghost" onClick={onCancel}>
          Cancel
        </KitButton>
        <KitButton
          variant="primary"
          isDisabled={!canConfirm}
          loading={isPending}
          onClick={() => onConfirm(resolveLifetimeChoice(weeksInput, effectiveOpenEnded))}
        >
          {confirmLabel}
        </KitButton>
      </div>
    </div>
  );
}
